import { useMutation, useQuery } from '@apollo/client/react';
import { APOLLO_FETCH_POLICY } from '@/constants/apollo';
import {
  LINK_I4_ACCOUNT,
  LINK_ILRONLINE_ACCOUNT,
  UNLINK_I4_ACCOUNT,
} from './mutations';
import {
  GET_I4_LINK_STATUS,
  GET_I4_REDIRECT_URL,
  GET_I4_DOWNLOADS,
  GET_I4_GUEST_REDIRECT_URL,
  GET_I4_LINK_URL,
  GET_I4_LINKS,
} from './queries';
import type {
  UserI4LinkDto,
  DocumentDto,
  LinkI4AccountInput,
  LinkIlrOnlineAccountInput,
  UnlinkI4AccountInput,
  I4RedirectUrlQueryInput,
  I4GuestRedirectUrlQueryInput,
  I4LinkUrlQueryInput,
} from './types';

/** Headers (X-Society-Id, X-Organisation-Id) come from Apollo societyContextLink reading usePublicSearchContext. */

export function useLinkI4Account() {
  return useMutation<
    { linkI4Account: UserI4LinkDto },
    { input: LinkI4AccountInput }
  >(LINK_I4_ACCOUNT);
}

export function useLinkIlrOnlineAccount() {
  return useMutation<
    { linkIlrOnlineAccount: UserI4LinkDto },
    { input: LinkIlrOnlineAccountInput }
  >(LINK_ILRONLINE_ACCOUNT);
}

export function useUnlinkI4Account() {
  return useMutation<
    { unlinkI4Account: boolean },
    { input: UnlinkI4AccountInput }
  >(UNLINK_I4_ACCOUNT);
}

export function useI4LinkStatus(societyCode: string, skip = false) {
  return useQuery<
    { i4LinkStatus: UserI4LinkDto | null },
    { societyCode: string }
  >(GET_I4_LINK_STATUS, {
    variables: { societyCode },
    skip: skip || !societyCode,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
}

export function useI4RedirectUrl(input: I4RedirectUrlQueryInput, skip = false) {
  return useQuery<
    { i4RedirectUrl: string },
    { input: I4RedirectUrlQueryInput }
  >(GET_I4_REDIRECT_URL, {
    variables: { input },
    skip: skip || !input.societyCode,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
}

export function useI4Downloads(societyCode: string, skip = false) {
  return useQuery<{ i4Downloads: DocumentDto[] }, { societyCode: string }>(
    GET_I4_DOWNLOADS,
    {
      variables: { societyCode },
      skip: skip || !societyCode,
      fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
    },
  );
}

export function useI4GuestRedirectUrl(
  input: I4GuestRedirectUrlQueryInput,
  skip = false,
) {
  return useQuery<
    { i4GuestRedirectUrl: string | null },
    { input: I4GuestRedirectUrlQueryInput }
  >(GET_I4_GUEST_REDIRECT_URL, {
    variables: { input },
    skip: skip || !input.societyCode,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
}

export function useI4LinkUrl(input: I4LinkUrlQueryInput, skip = false) {
  return useQuery<{ i4LinkUrl: string }, { input: I4LinkUrlQueryInput }>(
    GET_I4_LINK_URL,
    {
      variables: { input },
      skip: skip || !input.societyCode || !input.callbackUrl,
      // Use cache so prefetch from Downloads/Online Reg makes Start button instant on link-i4
      fetchPolicy: APOLLO_FETCH_POLICY.CACHE_FIRST,
    },
  );
}

export function useI4Links(skip = false) {
  return useQuery<{ i4Links: UserI4LinkDto[] }>(GET_I4_LINKS, {
    skip,
    fetchPolicy: APOLLO_FETCH_POLICY.NETWORK_ONLY,
  });
}
